import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { buildYearOptions } from "@/lib/appointmentYears";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CalendarDays, Clock, TrendingUp, BarChart3, Users, AlertTriangle,
  AlertCircle, Info, ArrowUpRight, ArrowDownRight, Activity, Zap,
  Calendar, Target, RefreshCw, ChevronRight, Gauge, PieChart as PieIcon, FileDown, Sun,
  DollarSign, UserPlus, UserCheck
} from "lucide-react";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, LineChart, Line, AreaChart, Area, Legend
} from "recharts";

const STATUS_LABELS = {
  pendiente_confirmacion: "Pendientes", confirmada: "Confirmadas",
  pendiente_reprogramacion: "Reprog.", cancelada_cliente: "Canc. Cliente",
  cancelada_taller: "Canc. Taller",
};
const STATUS_COLORS = {
  pendiente_confirmacion: "#f59e0b", confirmada: "#22c55e",
  pendiente_reprogramacion: "#3b82f6", cancelada_cliente: "#ef4444",
  cancelada_taller: "#94a3b8",
};
const SLOT_COLORS = ["#0ea5e9", "#10b981", "#8b5cf6", "#f97316", "#ec4899"];

function AlertBadge({ level }) {
  const cfg = {
    critical: { icon: AlertCircle, cls: "bg-red-100 text-red-700 border-red-200" },
    warning: { icon: AlertTriangle, cls: "bg-amber-100 text-amber-700 border-amber-200" },
    info: { icon: Info, cls: "bg-blue-100 text-blue-700 border-blue-200" },
  }[level] || { icon: Info, cls: "bg-slate-100 text-slate-600 border-slate-200" };
  const Icon = cfg.icon;
  return <Icon className="w-3.5 h-3.5 flex-shrink-0" />;
}

function KpiCard({ label, value, icon: Icon, color, sub, trend, onClick, traffic }) {
  const clickable = typeof onClick === "function";
  // Iter 216 — semáforo opcional: traffic={kind:'pendientes24h|ocupacion|cancelacion|confirmacion|sinAsesor', value:n}
  const sem = traffic ? semaforo(traffic.kind, traffic.value ?? value) : null;
  return (
    <Card
      className={`border-slate-200 rounded-sm ${sem ? sem.cls : ""} ${clickable ? "cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all" : ""}`}
      data-testid={`kpi-${label.replace(/\s/g, '-').toLowerCase()}`}
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${color}`}>
            <Icon className="w-4 h-4" />
          </div>
          {trend !== undefined && (
            <span className={`text-[10px] font-semibold flex items-center gap-0.5 ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
              {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(trend)}%
            </span>
          )}
        </div>
        <p className="text-2xl font-bold text-slate-900 font-mono mt-2 leading-none">{value}</p>
        <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium mt-1">{label}</p>
        {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
        {sem && (
          <div className="flex items-center gap-1 mt-1.5" data-testid={`traffic-${traffic.kind}-${sem.color}`}>
            <span className={`inline-block w-2 h-2 rounded-full ${sem.dot}`} />
            <span className="text-[9px] uppercase tracking-wider font-semibold text-slate-500">{sem.label}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OccupancyGauge({ pct, label, size = 120 }) {
  const color = pct > 80 ? "#ef4444" : pct > 50 ? "#f59e0b" : "#22c55e";
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="42" fill="none" stroke="#f1f5f9" strokeWidth="8" />
          <circle cx="50" cy="50" r="42" fill="none" stroke={color} strokeWidth="8"
            strokeDasharray={`${pct * 2.64} 264`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-bold font-mono text-slate-900">{pct}%</span>
        </div>
      </div>
      {label && <p className="text-[10px] text-slate-500 mt-1 text-center">{label}</p>}
    </div>
  );
}

// Iter 216 — Semáforos visuales para KPIs accionables.
// `kind` define la regla; `value` es el número a evaluar.
// Devuelve: { color: 'green|amber|red', cls: tailwind classes, label }.
function semaforo(kind, value) {
  const v = Number(value) || 0;
  if (kind === "pendientes24h") {
    if (v >= 9) return { color: "red", cls: "border-l-4 border-red-500", dot: "bg-red-500", label: "Requiere acción" };
    if (v >= 4) return { color: "amber", cls: "border-l-4 border-amber-400", dot: "bg-amber-400", label: "Atención" };
    return { color: "green", cls: "border-l-4 border-green-500", dot: "bg-green-500", label: "OK" };
  }
  if (kind === "ocupacion") {
    if (v > 95) return { color: "red", cls: "border-l-4 border-red-500", dot: "bg-red-500", label: "Saturado" };
    if (v >= 70 && v <= 90) return { color: "green", cls: "border-l-4 border-green-500", dot: "bg-green-500", label: "Óptimo" };
    if (v >= 40 && v < 70) return { color: "amber", cls: "border-l-4 border-amber-400", dot: "bg-amber-400", label: "Bajo" };
    return { color: "red", cls: "border-l-4 border-red-500", dot: "bg-red-500", label: "Muy bajo" };
  }
  if (kind === "cancelacion") {
    if (v > 20) return { color: "red", cls: "border-l-4 border-red-500", dot: "bg-red-500", label: "Alta" };
    if (v >= 10) return { color: "amber", cls: "border-l-4 border-amber-400", dot: "bg-amber-400", label: "Atención" };
    return { color: "green", cls: "border-l-4 border-green-500", dot: "bg-green-500", label: "OK" };
  }
  if (kind === "confirmacion") {
    if (v >= 80) return { color: "green", cls: "border-l-4 border-green-500", dot: "bg-green-500", label: "OK" };
    if (v >= 60) return { color: "amber", cls: "border-l-4 border-amber-400", dot: "bg-amber-400", label: "Atención" };
    return { color: "red", cls: "border-l-4 border-red-500", dot: "bg-red-500", label: "Baja" };
  }
  if (kind === "sinAsesor") {
    if (v >= 5) return { color: "red", cls: "border-l-4 border-red-500", dot: "bg-red-500", label: "Reasignar ya" };
    if (v >= 1) return { color: "amber", cls: "border-l-4 border-amber-400", dot: "bg-amber-400", label: "Asignar" };
    return { color: "green", cls: "border-l-4 border-green-500", dot: "bg-green-500", label: "OK" };
  }
  return { color: "", cls: "", dot: "", label: "" };
}

function AlertsPanel({ alerts, loading }) {
  if (loading) return <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-12 bg-slate-100 animate-pulse rounded-sm" />)}</div>;
  if (!alerts?.length) return <p className="text-xs text-slate-400 py-4 text-center">Sin alertas activas</p>;
  return (
    <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="alerts-panel">
      {alerts.map((a, i) => {
        const levelCls = {
          critical: "bg-red-50 border-red-200",
          warning: "bg-amber-50 border-amber-200",
          info: "bg-blue-50 border-blue-200",
        }[a.level] || "bg-slate-50 border-slate-200";
        return (
          <div key={i} className={`p-2.5 rounded-sm border flex items-start gap-2 ${levelCls}`} data-testid={`alert-${i}`}>
            <AlertBadge level={a.level} />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-slate-700 leading-relaxed">{a.message}</p>
              <span className="text-[9px] uppercase tracking-wider text-slate-400 font-medium">{a.category}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Iter 216 — Bloque "Acciones recomendadas". Genera una lista priorizada
// de acciones operativas en base a los umbrales del semáforo. Cada acción
// es clicable y abre Gestión de citas con el filtro adecuado.
function RecommendedActions({ periodSummary, globals, capacity, onOpen }) {
  const PC = periodSummary?.counts || {};
  const actions = [];
  const pend24 = PC.pendientes_mas_24h ?? 0;
  if (pend24 >= 9) {
    actions.push({ level: "red", text: `Confirma ${pend24} citas pendientes con más de 24h de espera (clientes esperando respuesta).`, filter: "pendiente_confirmacion" });
  } else if (pend24 >= 4) {
    actions.push({ level: "amber", text: `Revisa ${pend24} citas pendientes con más de 24h. Empiezan a acumular espera.`, filter: "pendiente_confirmacion" });
  }
  const sin = PC.sin_asesor ?? 0;
  if (sin >= 5) {
    actions.push({ level: "red", text: `Reasigna ${sin} citas activas sin asesor del periodo — alta carga sin propietario.`, filter: "todos" });
  } else if (sin >= 1) {
    actions.push({ level: "amber", text: `${sin} cita${sin > 1 ? "s" : ""} sin asesor en el periodo. Asignalas para evitar retrasos.`, filter: "todos" });
  }
  const ocup = capacity?.used && capacity?.total ? Math.round((capacity.used / capacity.total) * 100) : 0;
  if (ocup > 0 && ocup < 40) {
    actions.push({ level: "amber", text: `Ocupación de hoy ${ocup}% — quedan ${capacity.total - capacity.used} huecos libres. Buen momento para llamar a clientes con pendientes.`, filter: null });
  }
  const reprog = globals?.pending_reprogram ?? 0;
  if (reprog >= 3) {
    actions.push({ level: "amber", text: `${reprog} citas pendientes de reprogramar. Cierra el círculo antes de que el cliente pierda confianza.`, filter: "pendiente_reprogramacion" });
  }
  if (actions.length === 0) {
    return <p className="text-xs text-slate-500" data-testid="actions-empty">Sin acciones urgentes. Todo dentro de los umbrales operativos.</p>;
  }
  return (
    <ul className="space-y-2" data-testid="actions-list">
      {actions.map((a, i) => (
        <li
          key={i}
          className={`flex items-start gap-2 p-2 rounded-sm cursor-pointer hover:bg-slate-50 transition-colors border-l-4 ${a.level === "red" ? "border-red-500" : "border-amber-400"}`}
          onClick={a.filter ? () => onOpen(a.filter) : undefined}
          data-testid={`action-${i}`}
        >
          <span className={`inline-block w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${a.level === "red" ? "bg-red-500" : "bg-amber-400"}`} />
          <span className="text-xs text-slate-700 flex-1">{a.text}</span>
          {a.filter && <ArrowUpRight className="w-3.5 h-3.5 text-slate-400" />}
        </li>
      ))}
    </ul>
  );
}

// ─── APPOINTMENTS TAB ────────────────────────
function AppointmentsTab({ data, alerts, alertsLoading, periodSummary, periodLoading, periodValue, setPeriodValue, selectedCenter, navigate }) {
  if (!data) return null;
  const { kpis, capacity, by_slot, by_status, by_center } = data;

  const statusPie = Object.entries(by_status || {}).map(([k, v]) => ({
    name: STATUS_LABELS[k] || k, value: v, color: STATUS_COLORS[k] || "#94a3b8"
  }));

  const centerBar = Object.entries(by_center || {}).map(([k, v]) => ({ name: k || "Sin centro", count: v }));

  const slotData = (by_slot || []).map(s => ({
    ...s, free: s.free > 0 ? s.free : 0,
    fillColor: s.used >= s.capacity ? "#ef4444" : s.used > 0 ? "#f59e0b" : "#22c55e"
  }));

  // Iter 215: navegación a Gestión de citas con el filtro pre-aplicado.
  // Garantiza que los conteos sean trazables a la fuente.
  const openInManagement = (filter) => {
    const params = new URLSearchParams({ statusFilter: filter });
    if (selectedCenter) params.set("center", selectedCenter);
    if (navigate) navigate(`/admin/citas?${params.toString()}`);
  };

  const PC = periodSummary?.counts || {};
  const PCap = periodSummary?.capacity || {};

  return (
    <div className="space-y-5">
      {/* ─── BLOQUE 1 · OPERATIVO DE HOY ─── */}
      <div data-testid="dashboard-block-today">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-600">Operativo · Hoy / Mañana</h3>
          <span className="text-[10px] text-slate-400">Métricas fijas del día actual (no del periodo)</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiCard label="Citas hoy" value={kpis.today} icon={CalendarDays} color="text-red-600 bg-red-50" sub="Solo hoy" />
          <KpiCard label="Citas mañana" value={kpis.tomorrow} icon={Calendar} color="text-blue-600 bg-blue-50" sub="Solo mañana" />
          <KpiCard label="Huecos libres hoy" value={kpis.free_slots} icon={Target} color="text-green-600 bg-green-50" sub="Slots con hueco hoy" />
          <KpiCard label="Ocupación hoy" value={`${kpis.occupancy_pct}%`} icon={Gauge} color="text-slate-600 bg-slate-100" sub={`${capacity.used} / ${capacity.total} citas hoy`} />
          <KpiCard label="Capacidad total hoy" value={capacity.total} icon={Activity} color="text-cyan-600 bg-cyan-50" sub={`${capacity.per_slot} por franja`} />
        </div>
      </div>

      {/* ─── BLOQUE 2 · PERIODO SELECCIONADO ─── */}
      <div data-testid="dashboard-block-period">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <h3 className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-600">Periodo seleccionado</h3>
            {periodSummary?.label && (
              <span className="text-[10px] text-slate-500 font-mono">{periodSummary.label}{selectedCenter ? ` · ${selectedCenter}` : ' · todos los centros'}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5" data-testid="period-selector">
            {/* Iter 217 — Agenda futura en Operación diaria */}
            {[
              { v: "today", l: "Hoy" },
              { v: "next_7d", l: "Próx. 7d" },
              { v: "next_30d", l: "Próx. 30d" },
              { v: "next_60d", l: "Próx. 60d" },
            ].map(opt => (
              <button
                key={opt.v}
                onClick={() => setPeriodValue && setPeriodValue(opt.v)}
                className={`px-2.5 h-7 text-[10px] uppercase tracking-wider font-semibold rounded-sm border ${periodValue === opt.v ? "bg-slate-900 text-white border-slate-900" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                data-testid={`period-btn-${opt.v}`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>
        {periodLoading ? (
          <p className="text-xs text-slate-400">Cargando métricas del periodo…</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="period-cards">
            <KpiCard label="Total" value={PC.total ?? 0} icon={CalendarDays} color="text-slate-900 bg-slate-100" sub="Del periodo" onClick={() => openInManagement("todos")} />
            <KpiCard label="Pendientes" value={PC.pendientes ?? 0} icon={Clock} color="text-amber-700 bg-amber-50" sub="Del periodo" onClick={() => openInManagement("pendiente_confirmacion")} />
            <KpiCard label="Confirm. próximas" value={PC.confirmadas_proximas ?? 0} icon={Calendar} color="text-blue-700 bg-blue-50" sub="Fecha/hora ≥ ahora" onClick={() => openInManagement("confirmada_proximas")} />
            <KpiCard label="Confirm. pasadas" value={PC.confirmadas_pasadas ?? 0} icon={Calendar} color="text-slate-500 bg-slate-100" sub="Fecha/hora pasada" onClick={() => openInManagement("confirmada_pasadas")} />
            <KpiCard label="Reprogramadas" value={PC.reprogramadas ?? 0} icon={RefreshCw} color="text-purple-700 bg-purple-50" sub="Del periodo" onClick={() => openInManagement("pendiente_reprogramacion")} />
            <KpiCard label="Canceladas" value={PC.canceladas ?? 0} icon={Clock} color="text-red-700 bg-red-50" sub="Del periodo" onClick={() => openInManagement("cancelada")} />
            <KpiCard label="Sin asesor" value={PC.sin_asesor ?? 0} icon={Users} color="text-orange-700 bg-orange-50" sub="Activas sin asignar" traffic={{ kind: "sinAsesor" }} />
          </div>
        )}
        {!periodLoading && PCap.total > 0 && (
          <p className="text-[10px] text-slate-500 mt-2" data-testid="period-capacity-line">
            Capacidad del periodo: <strong>{PCap.total}</strong> citas posibles · usadas <strong>{PCap.used}</strong> · libres <strong>{PCap.free}</strong> · ocupación <strong>{PCap.occupancy_pct}%</strong>
          </p>
        )}
      </div>

      {/* ─── BLOQUE 3 · GLOBALES ─── */}
      <div data-testid="dashboard-block-global">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-600">Global (sin filtrar)</h3>
          <span className="text-[10px] text-slate-400">No depende del periodo</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard label="Pendientes >24h" value={PC.pendientes_mas_24h ?? 0} icon={Clock} color="text-amber-600 bg-amber-50" sub="Espera del cliente > 24h" onClick={() => openInManagement("pendiente_confirmacion")} traffic={{ kind: "pendientes24h" }} />
          <KpiCard label="Pendientes confirmar globales" value={kpis.pending_confirm} icon={Clock} color="text-amber-600 bg-amber-50" sub="Total histórico sin acotar" onClick={() => openInManagement("pendiente_confirmacion")} />
          <KpiCard label="Pendientes reprog. globales" value={kpis.pending_reprogram} icon={RefreshCw} color="text-purple-600 bg-purple-50" sub="Total histórico sin acotar" onClick={() => openInManagement("pendiente_reprogramacion")} />
          <KpiCard label="Vacaciones pendientes" value={kpis.vacation_pending ?? 0} icon={Sun} color="text-yellow-600 bg-yellow-50" sub="Solicitudes por aprobar" />
        </div>
      </div>

      {/* ─── BLOQUE 4 · ACCIONES RECOMENDADAS (Iter 216) ─── */}
      <Card className="border-slate-200 rounded-sm" data-testid="dashboard-block-actions">
        <CardContent className="p-4">
          <h3 className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-600 mb-3">Acciones recomendadas</h3>
          <RecommendedActions
            periodSummary={periodSummary}
            globals={kpis}
            capacity={capacity}
            onOpen={openInManagement}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Occupancy + Alerts */}
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <Gauge className="w-4 h-4 text-red-600" /> Ocupacion Hoy
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center pt-2">
            <OccupancyGauge pct={kpis.occupancy_pct} label={`${capacity.used} / ${capacity.total} citas`} />
          </CardContent>
        </Card>

        <Card className="border-slate-200 rounded-sm" data-testid="status-pie">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <PieIcon className="w-4 h-4 text-red-600" /> Estado de Citas Hoy
            </CardTitle>
          </CardHeader>
          <CardContent className="h-52">
            {statusPie.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusPie} cx="50%" cy="50%" innerRadius={35} outerRadius={65} dataKey="value" strokeWidth={2}>
                    {statusPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: "11px", borderRadius: "2px" }} />
                  <Legend wrapperStyle={{ fontSize: "10px" }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-slate-400 text-center pt-16">Sin datos</p>}
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card className="border-slate-200 rounded-sm" data-testid="alerts-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" /> Alertas
              {alerts?.length > 0 && <Badge variant="outline" className="ml-auto text-[9px] bg-red-50 text-red-600 border-red-200">{alerts.length}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <AlertsPanel alerts={alerts} loading={alertsLoading} />
          </CardContent>
        </Card>
      </div>

      {/* Slot usage chart */}
      <Card className="border-slate-200 rounded-sm" data-testid="slot-usage-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-red-600" /> Ocupacion por Franja Horaria
          </CardTitle>
        </CardHeader>
        <CardContent className="h-64">
          {slotData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={slotData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="slot" tick={{ fontSize: 9 }} interval={2} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: "11px", borderRadius: "2px" }}
                  formatter={(val, name) => [val, name === "used" ? "Ocupadas" : "Libres"]} />
                <Bar dataKey="used" stackId="a" fill="#dc2626" radius={[0, 0, 0, 0]} name="Ocupadas" />
                <Bar dataKey="free" stackId="a" fill="#e2e8f0" radius={[2, 2, 0, 0]} name="Libres" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center pt-20">Sin datos</p>}
        </CardContent>
      </Card>

      {/* Center bar */}
      {centerBar.length > 0 && (
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-red-600" /> Citas por Centro (Hoy)
            </CardTitle>
          </CardHeader>
          <CardContent className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={centerBar} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={80} />
                <Tooltip contentStyle={{ fontSize: "11px", borderRadius: "2px" }} />
                <Bar dataKey="count" radius={[0, 3, 3, 0]}>
                  {centerBar.map((_, i) => <Cell key={i} fill={SLOT_COLORS[i % SLOT_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── MANAGEMENT TAB ──────────────────────────
function ManagementTab({ data, forecast, deadSlots, pressure, conversion, period, onPeriodChange, periodSummary, selectedCenter, navigate }) {
  if (!data) return null;
  const { kpis, by_day, services_top, centers, week_capacity, comparison, clients, revenue, saturation, advisor_workload } = data;

  // Iter 216 — KPIs accionables con semáforos al inicio del tab.
  const PC = periodSummary?.counts || {};
  const PR = periodSummary?.rates || {};
  const PCap = periodSummary?.capacity || {};
  const openInMgmt = (filter) => {
    if (!navigate) return;
    const params = new URLSearchParams({ statusFilter: filter });
    if (selectedCenter) params.set("center", selectedCenter);
    navigate(`/admin/citas?${params.toString()}`);
  };

  const CLIENT_COLORS = ["#3b82f6", "#22c55e"];
  const goalColor = (pct) => pct > 100 ? "bg-blue-500" : pct >= 80 ? "bg-green-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";
  const goalTextColor = (pct) => pct > 100 ? "text-blue-600" : pct >= 80 ? "text-green-600" : pct >= 50 ? "text-amber-600" : "text-red-500";
  const satBarColor = (pct) => pct > 85 ? "bg-red-500" : pct > 60 ? "bg-amber-500" : "bg-green-500";
  const satTextColor = (pct) => pct > 85 ? "text-red-500" : pct > 60 ? "text-amber-600" : "text-green-600";

  return (
    <div className="space-y-5">
      {/* Period selector */}
      <div className="flex items-center justify-between" data-testid="gerencia-period-selector">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Periodo:</span>
          {[
            { v: "week", l: "Semana" },
            { v: "month", l: "Mes" },
            { v: "quarter", l: "Trimestre" },
            { v: "year", l: "Ano" },
          ].map(p => (
            <button key={p.v} onClick={() => onPeriodChange(p.v)}
              className={`px-3 py-1.5 text-xs rounded-sm font-semibold uppercase tracking-wider transition-colors ${
                period === p.v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid={`period-btn-${p.v}`}
            >{p.l}</button>
          ))}
        </div>
        <span className="text-[10px] text-slate-400 font-mono" data-testid="period-range">
          {data.period?.start} — {data.period?.end}
        </span>
      </div>

      {/* Iter 216 — KPIs accionables con semáforos */}
      <div data-testid="gerencia-kpis-traffic">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-600">KPIs accionables</h3>
          <span className="text-[10px] text-slate-400">Con semáforos verde/ámbar/rojo</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiCard label="Total periodo" value={PC.total ?? 0} icon={CalendarDays} color="text-slate-900 bg-slate-100" sub="Citas del periodo" onClick={() => openInMgmt("todos")} />
          <KpiCard label="Tasa confirmación" value={`${PR.tasa_confirmacion_pct ?? 0}%`} icon={Target} color="text-green-700 bg-green-50" sub="Confirmadas / total" traffic={{ kind: "confirmacion", value: PR.tasa_confirmacion_pct }} />
          <KpiCard label="Tasa cancelación" value={`${PR.tasa_cancelacion_pct ?? 0}%`} icon={AlertCircle} color="text-red-700 bg-red-50" sub="Canceladas / total" onClick={() => openInMgmt("cancelada")} traffic={{ kind: "cancelacion", value: PR.tasa_cancelacion_pct }} />
          <KpiCard label="Ocupación periodo" value={`${PCap.occupancy_pct ?? 0}%`} icon={Gauge} color="text-blue-700 bg-blue-50" sub={`${PCap.used ?? 0} / ${PCap.total ?? 0} citas`} traffic={{ kind: "ocupacion", value: PCap.occupancy_pct }} />
          <KpiCard label="Sin asesor" value={PC.sin_asesor ?? 0} icon={Users} color="text-orange-700 bg-orange-50" sub="Activas sin asignar" onClick={() => openInMgmt("todos")} traffic={{ kind: "sinAsesor" }} />
        </div>
      </div>

      {/* Comparison cards */}
      {comparison && (
        <div data-testid="comparison-section">
          <p className="text-[10px] text-slate-400 mb-2 uppercase tracking-wider font-medium">{comparison.label}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { key: "total", label: "Total Citas", icon: CalendarDays, positive: "up" },
              { key: "confirmadas", label: "Confirmadas", icon: Target, positive: "up" },
              { key: "canceladas", label: "Canceladas", icon: AlertCircle, positive: "down" },
              { key: "reprogramadas", label: "Reprogramadas", icon: RefreshCw, positive: "down" },
            ].map(item => {
              const d = comparison[item.key];
              const isGood = item.positive === "up" ? d.change >= 0 : d.change <= 0;
              const color = d.change === 0 ? "text-slate-400" : isGood ? "text-green-600" : "text-red-500";
              const bgColor = d.change === 0 ? "bg-slate-50" : isGood ? "bg-green-50" : "bg-red-50";
              const Icon = item.icon;
              return (
                <Card key={item.key} className={`border-slate-200 rounded-sm ${bgColor}`} data-testid={`compare-${item.key}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-1">
                      <Icon className="w-4 h-4 text-slate-400" />
                      <div className={`flex items-center gap-0.5 ${color}`}>
                        {d.change > 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : d.change < 0 ? <ArrowDownRight className="w-3.5 h-3.5" /> : null}
                        <span className="text-xs font-bold">{d.change > 0 ? "+" : ""}{d.change}%</span>
                      </div>
                    </div>
                    <p className="text-xl font-bold font-mono text-slate-900">{d.current}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">{item.label}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Anterior: {d.previous}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Revenue Goal + Forecast + Saturation */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue Goal */}
        <Card className="border-slate-200 rounded-sm" data-testid="revenue-goal-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-600" /> Objetivo de Ingresos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {revenue?.goal > 0 ? (
              <div className="space-y-3">
                <div>
                  <p className={`text-2xl font-bold font-mono ${goalTextColor(revenue.goal_pct)}`} data-testid="revenue-estimated">
                    {revenue.estimated.toLocaleString("es-ES")} &euro;
                  </p>
                  <p className="text-xs text-slate-400">de {revenue.goal.toLocaleString("es-ES")} &euro;</p>
                </div>
                <div>
                  <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden" data-testid="revenue-progress-bar">
                    <div className={`h-3 rounded-full transition-all ${goalColor(revenue.goal_pct)}`}
                      style={{ width: `${Math.min(revenue.goal_pct, 100)}%` }} />
                  </div>
                  <p className={`text-right text-sm font-bold font-mono mt-1 ${goalTextColor(revenue.goal_pct)}`} data-testid="revenue-goal-pct">
                    {revenue.goal_pct}%
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="bg-slate-50 p-2 rounded-sm border border-slate-100">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Confirmadas</p>
                    <p className="text-sm font-bold font-mono">{revenue.confirmed_count}</p>
                  </div>
                  <div className="bg-slate-50 p-2 rounded-sm border border-slate-100">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Ticket medio</p>
                    <p className="text-sm font-bold font-mono">{revenue.ticket_medio} &euro;</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <DollarSign className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-xs text-slate-500">Configura el objetivo en</p>
                <p className="text-xs font-semibold text-slate-600">Configuracion &rarr; Negocio</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue Forecast */}
        <Card className="border-slate-200 rounded-sm" data-testid="revenue-forecast-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-indigo-600" /> Prevision Fin de Periodo
            </CardTitle>
          </CardHeader>
          <CardContent>
            {revenue?.ticket_medio > 0 ? (
              <div className="space-y-3">
                <div>
                  <p className={`text-2xl font-bold font-mono ${goalTextColor(revenue.forecast_pct)}`} data-testid="forecast-revenue">
                    {revenue.forecast.toLocaleString("es-ES")} &euro;
                  </p>
                  <p className="text-xs text-slate-400">prevision estimada</p>
                </div>
                {revenue.goal > 0 && (
                  <div>
                    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div className={`h-2 rounded-full transition-all ${goalColor(revenue.forecast_pct)}`}
                        style={{ width: `${Math.min(revenue.forecast_pct, 100)}%` }} />
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">{revenue.forecast_pct}% del objetivo</p>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-1.5 text-center">
                  <div className="bg-slate-50 p-2 rounded-sm border border-slate-100">
                    <p className="text-[9px] text-slate-500 uppercase">Avg/dia</p>
                    <p className="text-xs font-bold font-mono">{revenue.daily_avg.toLocaleString("es-ES")} &euro;</p>
                  </div>
                  <div className="bg-slate-50 p-2 rounded-sm border border-slate-100">
                    <p className="text-[9px] text-slate-500 uppercase">Dia {revenue.days_elapsed}</p>
                    <p className="text-xs font-bold font-mono">de {revenue.days_total}</p>
                  </div>
                  <div className="bg-slate-50 p-2 rounded-sm border border-slate-100">
                    <p className="text-[9px] text-slate-500 uppercase">Restantes</p>
                    <p className="text-xs font-bold font-mono">{revenue.days_remaining} dias</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <TrendingUp className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-xs text-slate-500">Configura el ticket medio</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Taller Saturation */}
        <Card className="border-slate-200 rounded-sm" data-testid="saturation-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <Gauge className="w-4 h-4 text-amber-600" /> Saturacion del Taller
            </CardTitle>
          </CardHeader>
          <CardContent>
            {saturation ? (
              <div className="space-y-3">
                <div className="text-center">
                  <p className={`text-4xl font-bold font-mono ${satTextColor(saturation.pct)}`} data-testid="saturation-pct">
                    {saturation.pct}%
                  </p>
                  <p className="text-xs text-slate-400">ocupacion</p>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden" data-testid="saturation-progress-bar">
                  <div className={`h-3 rounded-full transition-all ${satBarColor(saturation.pct)}`}
                    style={{ width: `${Math.min(saturation.pct, 100)}%` }} />
                </div>
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="bg-slate-50 p-2.5 rounded-sm border border-slate-100">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Capacidad</p>
                    <p className="text-lg font-bold font-mono text-slate-900">{saturation.capacity}</p>
                  </div>
                  <div className="bg-slate-50 p-2.5 rounded-sm border border-slate-100">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Activas</p>
                    <p className="text-lg font-bold font-mono text-slate-900">{saturation.confirmed}</p>
                  </div>
                </div>
              </div>
            ) : <p className="text-xs text-slate-400 text-center py-8">Sin datos</p>}
          </CardContent>
        </Card>
      </div>

      {/* KPIs row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="gerencia-kpis">
        <KpiCard label="Ocup. Semana" value={`${kpis.week_occupancy}%`} icon={Gauge} color="text-cyan-600 bg-cyan-50" />
        <KpiCard label="Ocup. Periodo" value={`${kpis.period_occupancy}%`} icon={Activity} color="text-indigo-600 bg-indigo-50" />
        <KpiCard label="Tasa Confirm." value={`${kpis.confirm_rate}%`} icon={Target} color="text-green-600 bg-green-50" />
        <KpiCard label="Tasa Cancel." value={`${kpis.cancel_rate}%`} icon={AlertTriangle} color="text-amber-600 bg-amber-50" />
        <KpiCard label="Fidelizacion" value={`${clients?.fidelity_rate || 0}%`} icon={UserCheck} color="text-purple-600 bg-purple-50" sub="Recurrentes" />
      </div>

      {/* Advisor Workload + Clients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Advisor workload */}
        <Card className="border-slate-200 rounded-sm" data-testid="advisor-workload-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-red-600" /> Citas por Asesor
            </CardTitle>
          </CardHeader>
          <CardContent>
            {advisor_workload?.length > 0 ? (
              <div className="space-y-3">
                {advisor_workload.map((a, i) => {
                  const maxTotal = advisor_workload[0]?.total || 1;
                  const pct = Math.round((a.total / maxTotal) * 100);
                  return (
                    <div key={i} data-testid={`advisor-bar-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-slate-700 truncate">{a.name}</span>
                        <span className="text-xs font-mono font-bold text-slate-900 flex-shrink-0 ml-2">{a.total}</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden relative">
                        <div className="bg-red-500 h-4 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        <div className="bg-green-500 h-4 rounded-full absolute top-0 left-0 transition-all" style={{ width: `${Math.round((a.confirmed / maxTotal) * 100)}%` }} />
                      </div>
                      <div className="flex gap-2 mt-0.5">
                        <span className="text-[10px] text-green-600">{a.confirmed} conf.</span>
                        <span className="text-[10px] text-slate-400">{a.total - a.confirmed} otras</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-xs text-slate-400 text-center py-8">Sin datos de asesores</p>}
          </CardContent>
        </Card>

        {/* Clients: new vs recurrent + VIP + Fidelity */}
        <Card className="border-slate-200 rounded-sm" data-testid="clients-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" /> Clientes
              {clients?.vip_count > 0 && (
                <Badge className="ml-auto bg-amber-100 text-amber-700 border-amber-200 text-[9px]" data-testid="vip-badge">
                  VIP: {clients.vip_count}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {clients?.total > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div className="w-28 h-28 flex-shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={[{ name: "Nuevos", value: clients.new }, { name: "Recurrentes", value: clients.recurrent }]}
                          cx="50%" cy="50%" innerRadius={25} outerRadius={45} dataKey="value" startAngle={90} endAngle={-270}>
                          {CLIENT_COLORS.map((c, i) => <Cell key={i} fill={c} />)}
                        </Pie>
                        <Tooltip contentStyle={{ fontSize: "11px", borderRadius: "2px" }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-sm bg-blue-500" />
                      <span className="text-xs text-slate-700 flex-1">Nuevos</span>
                      <span className="text-xs font-bold font-mono" data-testid="clients-new-count">{clients.new} ({clients.new_pct}%)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-sm bg-green-500" />
                      <span className="text-xs text-slate-700 flex-1">Recurrentes</span>
                      <span className="text-xs font-bold font-mono" data-testid="clients-recurrent-count">{clients.recurrent} ({clients.recurrent_pct}%)</span>
                    </div>
                    <div className="border-t border-slate-100 pt-2 mt-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-slate-500 uppercase">Fidelizacion</span>
                        <span className="text-xs font-bold text-purple-600" data-testid="fidelity-rate">{clients.fidelity_rate}%</span>
                      </div>
                      {clients.vip_count > 0 && (
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-[10px] text-amber-600 uppercase">Clientes VIP</span>
                          <span className="text-xs font-bold text-amber-600" data-testid="vip-count">{clients.vip_count}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-400">{clients.total} clientes unicos</p>
                  </div>
                </div>
              </div>
            ) : <p className="text-xs text-slate-400 text-center py-8">Sin datos</p>}
          </CardContent>
        </Card>
      </div>

      {/* Conversion funnel */}
      {conversion && (
        <Card className="border-slate-200 rounded-sm" data-testid="conversion-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-red-600" /> Embudo de Conversion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { label: "Solicitudes", val: conversion.total_requests, cls: "bg-slate-900 text-white" },
                { label: "Confirmadas", val: conversion.confirmed, cls: "bg-green-600 text-white" },
                { label: "Pendientes", val: conversion.pending, cls: "bg-amber-500 text-white" },
                { label: "Reprogramacion", val: conversion.reprogrammed, cls: "bg-blue-500 text-white" },
                { label: "Canceladas", val: conversion.cancelled, cls: "bg-red-500 text-white" },
              ].map((s, i) => (
                <div key={i} className={`p-3 rounded-sm text-center ${s.cls}`}>
                  <p className="text-lg font-bold font-mono">{s.val}</p>
                  <p className="text-[10px] uppercase tracking-wider opacity-80">{s.label}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-slate-600">
              <span>Tasa conversion: <strong className="text-green-600">{conversion.conversion_rate}%</strong></span>
              <span>Tasa perdida: <strong className="text-red-500">{conversion.loss_rate}%</strong></span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Evolution chart */}
      <Card className="border-slate-200 rounded-sm" data-testid="evolution-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-red-600" /> Evolucion de Citas ({data.period?.label})
          </CardTitle>
        </CardHeader>
        <CardContent className="h-64">
          {by_day?.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={by_day} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <defs>
                  <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#dc2626" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: "11px", borderRadius: "2px" }} />
                <Legend wrapperStyle={{ fontSize: "10px" }} />
                <Area type="monotone" dataKey="total" stroke="#dc2626" fill="url(#colorTotal)" strokeWidth={2} name="Total" />
                <Line type="monotone" dataKey="confirmed" stroke="#22c55e" strokeWidth={1.5} dot={false} name="Confirmadas" />
                <Line type="monotone" dataKey="cancelled" stroke="#ef4444" strokeWidth={1.5} dot={false} strokeDasharray="4 2" name="Canceladas" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center pt-20">Sin datos para este periodo</p>}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Services */}
        <Card className="border-slate-200 rounded-sm" data-testid="services-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-red-600" /> Servicios mas Demandados
            </CardTitle>
          </CardHeader>
          <CardContent className="h-56">
            {services_top?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={services_top} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="service" tick={{ fontSize: 8 }} interval={0} angle={-15} textAnchor="end" height={45} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: "11px", borderRadius: "2px" }} />
                  <Bar dataKey="count" fill="#dc2626" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-slate-400 text-center pt-16">Sin datos</p>}
          </CardContent>
        </Card>

        {/* Centers comparison */}
        <Card className="border-slate-200 rounded-sm" data-testid="centers-compare">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-red-600" /> Comparativa por Centro
            </CardTitle>
          </CardHeader>
          <CardContent>
            {centers?.length > 0 ? (
              <div className="space-y-3">
                {centers.map((c, i) => (
                  <div key={i} className="border border-slate-100 p-3 rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-slate-700">{c.center}</span>
                      <span className="text-xs font-mono font-bold text-slate-900">{c.total} total</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded-sm">{c.confirmed} conf.</span>
                      <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-sm">{c.pending} pend.</span>
                      <span className="text-[10px] text-red-600 bg-red-50 px-1.5 py-0.5 rounded-sm">{c.cancelled} canc.</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-slate-400 text-center py-6">Sin datos</p>}
          </CardContent>
        </Card>
      </div>

      {/* Forecast */}
      {forecast?.forecast?.length > 0 && (
        <Card className="border-slate-200 rounded-sm" data-testid="forecast-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-red-600" /> Prevision de Carga (7 dias)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {forecast.forecast.map((d, i) => {
                const occ = d.occupancy_pct;
                const bg = d.closed ? "bg-slate-100" : occ > 80 ? "bg-red-50 border-red-200" : occ > 50 ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200";
                return (
                  <div key={i} className={`p-3 rounded-sm border text-center ${bg}`} data-testid={`forecast-day-${i}`}>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{d.weekday}</p>
                    <p className="text-[10px] font-mono text-slate-400">{(() => { const p = d.date?.split("-"); return p?.length === 3 ? `${p[2]}-${p[1]}` : d.date?.slice(5); })()}</p>
                    {d.closed ? (
                      <p className="text-xs text-slate-400 mt-1">Cerrado</p>
                    ) : (
                      <>
                        <p className="text-lg font-bold font-mono text-slate-900 mt-1">{occ}%</p>
                        <p className="text-[10px] text-slate-500">{d.booked}/{d.capacity}</p>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dead slots + Pressure */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {deadSlots && (
          <Card className="border-slate-200 rounded-sm" data-testid="dead-slots-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
                <Target className="w-4 h-4 text-red-600" /> Huecos Muertos
                <Badge variant="outline" className="ml-auto text-[9px]">{deadSlots.total_dead} total</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="h-52">
              {deadSlots.by_day?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={deadSlots.by_day} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={v => v.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: "11px", borderRadius: "2px" }} />
                    <Bar dataKey="dead_slots" fill="#94a3b8" radius={[2, 2, 0, 0]} name="Huecos" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-slate-400 text-center pt-16">Sin datos</p>}
            </CardContent>
          </Card>
        )}

        {pressure && !pressure.closed && (
          <Card className="border-slate-200 rounded-sm" data-testid="pressure-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" /> Presion de Agenda Hoy
                <Badge variant="outline" className="ml-auto text-[9px]">{pressure.overall_pressure}%</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="h-52">
              {pressure.slots?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pressure.slots.filter((_, i) => i % 3 === 0)} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="slot" tick={{ fontSize: 9 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: "11px", borderRadius: "2px" }} />
                    <Bar dataKey="requests" fill="#f59e0b" radius={[2, 2, 0, 0]} name="Solicitudes" />
                    <Bar dataKey="active" fill="#22c55e" radius={[2, 2, 0, 0]} name="Activas" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-slate-400 text-center pt-16">Sin datos</p>}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ─── ADVISORS TAB ────────────────────────────
function AdvisorsTab({ data, advisorsSummary, selectedCenter, navigate }) {
  if (!data) return null;
  const { advisors, unassigned, occupancy_today, total_slots_today } = data;
  // Iter 216 — Tabla "Carga por asesor" con semáforos + click para abrir Gestión.
  const summaryRows = advisorsSummary?.advisors || [];
  const media = advisorsSummary?.media_carga_futura ?? 0;

  const openByAdvisor = (advId, statusFilter) => {
    if (!navigate) return;
    const params = new URLSearchParams({ statusFilter });
    if (advId) params.set("advisor", advId);
    if (selectedCenter) params.set("center", selectedCenter);
    navigate(`/admin/citas?${params.toString()}`);
  };

  return (
    <div className="space-y-5">
      {/* Iter 216 — Resumen carga del periodo con desviación contra media */}
      {summaryRows.length > 0 && (
        <Card className="border-slate-200 rounded-sm" data-testid="advisors-load-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-red-600" /> Carga por asesor — {advisorsSummary.label}
              <span className="text-[10px] text-slate-400 font-normal ml-auto">Media carga futura: <strong>{media}</strong> citas/asesor</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="advisors-load-table">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="text-left py-2 px-2">Asesor</th>
                    <th className="text-right py-2 px-2">Total</th>
                    <th className="text-right py-2 px-2">Pendientes</th>
                    <th className="text-right py-2 px-2">Pend. &gt;24h</th>
                    <th className="text-right py-2 px-2">Confirmadas</th>
                    <th className="text-right py-2 px-2">Canceladas</th>
                    <th className="text-right py-2 px-2">Ocup. hoy</th>
                    <th className="text-right py-2 px-2">Ocup. semana</th>
                    <th className="text-right py-2 px-2">Carga futura</th>
                    <th className="text-right py-2 px-2">Desv. media</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.map(r => {
                    const sem24 = semaforo("pendientes24h", r.pendientes_mas_24h);
                    const devColor = Math.abs(r.desviacion_vs_media) > Math.max(media * 0.4, 3)
                      ? "text-red-600 font-bold"
                      : Math.abs(r.desviacion_vs_media) > Math.max(media * 0.2, 2)
                      ? "text-amber-600 font-semibold"
                      : "text-slate-700";
                    return (
                      <tr key={r.advisor_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`advisor-row-${r.advisor_id}`}>
                        <td className="py-2 px-2 font-medium text-slate-700">{r.advisor_name}</td>
                        <td className="py-2 px-2 text-right font-mono">{r.total}</td>
                        <td className="py-2 px-2 text-right font-mono">{r.pendientes}</td>
                        <td className="py-2 px-2 text-right font-mono">
                          <span className={`inline-flex items-center gap-1 ${sem24.color === "red" ? "text-red-600 font-bold" : sem24.color === "amber" ? "text-amber-600 font-semibold" : "text-slate-700"}`}>
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${sem24.dot}`} />
                            {r.pendientes_mas_24h}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right font-mono">{r.confirmadas}</td>
                        <td className="py-2 px-2 text-right font-mono">{r.canceladas}</td>
                        <td className="py-2 px-2 text-right font-mono">{r.ocupacion_hoy}</td>
                        <td className="py-2 px-2 text-right font-mono">{r.ocupacion_semana}</td>
                        <td className="py-2 px-2 text-right font-mono">{r.carga_futura}</td>
                        <td className={`py-2 px-2 text-right font-mono ${devColor}`}>
                          {r.desviacion_vs_media > 0 ? `+${r.desviacion_vs_media}` : r.desviacion_vs_media}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[10px] text-slate-400 mt-2">
                Desv. media positiva = más carga que la media (saturación potencial).
                Desv. media negativa = menos carga (capacidad disponible).
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <KpiCard label="Asesores Activos" value={advisors?.length || 0} icon={Users} color="text-blue-600 bg-blue-50" />
        <KpiCard label="Sin Asesor" value={unassigned} icon={AlertTriangle} color="text-amber-600 bg-amber-50" traffic={{ kind: "sinAsesor" }} />
        <KpiCard label="Slots Hoy" value={total_slots_today} icon={Clock} color="text-slate-600 bg-slate-100" />
        <KpiCard label="Total Citas Periodo" value={advisors?.reduce((s, a) => s + a.total, 0) || 0} icon={CalendarDays} color="text-red-600 bg-red-50" />
      </div>

      {/* Occupancy today per advisor */}
      {occupancy_today?.length > 0 && (
        <Card className="border-slate-200 rounded-sm" data-testid="advisor-occupancy">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <Gauge className="w-4 h-4 text-red-600" /> Ocupacion por Asesor (Hoy)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {occupancy_today.map((a, i) => (
                <div key={i} className="flex flex-col items-center p-3 border border-slate-100 rounded-sm" data-testid={`advisor-occ-${i}`}>
                  <OccupancyGauge pct={a.occupancy_pct} size={80} />
                  <p className="text-xs font-semibold text-slate-700 mt-2 text-center truncate w-full">{a.name}</p>
                  <p className="text-[10px] text-slate-400">{a.appointments}/{a.total_slots}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Advisor stats table */}
      <Card className="border-slate-200 rounded-sm" data-testid="advisor-stats-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
            <Users className="w-4 h-4 text-red-600" /> Rendimiento por Asesor
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 text-slate-500 font-semibold uppercase tracking-wider">Asesor</th>
                  <th className="text-left py-2 px-2 text-slate-500 font-semibold uppercase tracking-wider">Centro</th>
                  <th className="text-center py-2 px-1 text-slate-500 font-semibold uppercase tracking-wider">Total</th>
                  <th className="text-center py-2 px-1 text-slate-500 font-semibold uppercase tracking-wider">Conf.</th>
                  <th className="text-center py-2 px-1 text-slate-500 font-semibold uppercase tracking-wider">Pend.</th>
                  <th className="text-center py-2 px-1 text-slate-500 font-semibold uppercase tracking-wider">Reprog.</th>
                  <th className="text-center py-2 px-1 text-slate-500 font-semibold uppercase tracking-wider">Canc.</th>
                </tr>
              </thead>
              <tbody>
                {advisors?.map((a, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <td className="py-2 px-2 font-medium text-slate-900">{a.name}</td>
                    <td className="py-2 px-2 text-slate-500">{a.center || "-"}</td>
                    <td className="py-2 px-1 text-center font-mono font-bold text-slate-900">{a.total}</td>
                    <td className="py-2 px-1 text-center font-mono text-green-600">{a.confirmed}</td>
                    <td className="py-2 px-1 text-center font-mono text-amber-600">{a.pending}</td>
                    <td className="py-2 px-1 text-center font-mono text-blue-600">{a.reprogrammed}</td>
                    <td className="py-2 px-1 text-center font-mono text-red-500">{a.cancelled}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Advisor bar chart */}
      {advisors?.length > 0 && (
        <Card className="border-slate-200 rounded-sm" data-testid="advisor-bar-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-red-600" /> Carga de Trabajo
            </CardTitle>
          </CardHeader>
          <CardContent className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={advisors} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: "11px", borderRadius: "2px" }} />
                <Legend wrapperStyle={{ fontSize: "10px" }} />
                <Bar dataKey="confirmed" stackId="a" fill="#22c55e" name="Confirmadas" />
                <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pendientes" />
                <Bar dataKey="reprogrammed" stackId="a" fill="#3b82f6" name="Reprog." />
                <Bar dataKey="cancelled" stackId="a" fill="#ef4444" name="Canceladas" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── MAIN DASHBOARD ──────────────────────────
export default function DashboardPage() {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("citas");
  const [center, setCenter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [centers, setCenters] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [mgtPeriod, setMgtPeriod] = useState("month");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Data states
  const [aptData, setAptData] = useState(null);
  const [mgtData, setMgtData] = useState(null);
  const [advData, setAdvData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [forecast, setForecast] = useState(null);
  const [deadSlots, setDeadSlots] = useState(null);
  const [pressure, setPressure] = useState(null);
  const [conversion, setConversion] = useState(null);
  // Iter 215/217 — Selector de periodo del bloque "Operación diaria". Default
  // 'next_30d' (agenda futura, decisión Iter 217). En Gerencia se usa mgtPeriod
  // que sigue siendo natural (month/quarter/year) para análisis histórico.
  const [aptPeriod, setAptPeriod] = useState("next_30d");
  const [periodSummary, setPeriodSummary] = useState(null);
  const [periodLoading, setPeriodLoading] = useState(true);
  // Iter 216 — Estado del resumen por asesor (tab Asesores).
  const [advisorsSummary, setAdvisorsSummary] = useState(null);
  // Iter 162 — Status por tab para gestionar 403/500/empty sin pantalla en blanco
  const [tabStatus, setTabStatus] = useState({ citas: "loading", gerencia: "loading", asesores: "loading" });
  const _classifyError = (e) => (e?.response?.status === 403 ? "forbidden" : "error");

  const centerParam = center !== "all" ? center : "";

  const loadCenters = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/centers");
      setCenters(res.data.filter(c => c.active));
    } catch {}
  }, [axiosAuth]);

  const loadAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const res = await axiosAuth().get("/analytics/alerts", { params: { center: centerParam || undefined } });
      setAlerts(res.data.alerts || []);
    } catch { setAlerts([]); }
    finally { setAlertsLoading(false); }
  }, [axiosAuth, centerParam]);

  const loadAppointments = useCallback(async () => {
  // Iter 162 — Estado por tab para gestionar 403/500/empty sin pantalla en blanco
    try {
      const res = await axiosAuth().get("/analytics/appointments", { params: { center: centerParam || undefined } });
      setAptData(res.data);
      setTabStatus(s => ({ ...s, citas: "ok" }));
    } catch (e) {
      setAptData(null);
      setTabStatus(s => ({ ...s, citas: _classifyError(e) }));
    }
  }, [axiosAuth, centerParam]);

  // Iter 215/216 — Resumen del PERIODO seleccionado, alineado 1:1 con Gestión de citas.
  // Acepta override de periodo (citas usa aptPeriod, gerencia usa mgtPeriod).
  const loadPeriodSummary = useCallback(async (periodOverride) => {
    setPeriodLoading(true);
    try {
      const res = await axiosAuth().get("/analytics/period-summary", {
        params: {
          period: periodOverride || aptPeriod,
          year: selectedYear || undefined,
          center: centerParam || undefined,
        },
      });
      setPeriodSummary(res.data);
    } catch (e) {
      setPeriodSummary(null);
    } finally {
      setPeriodLoading(false);
    }
  }, [axiosAuth, aptPeriod, selectedYear, centerParam]);

  const loadManagement = useCallback(async () => {
    try {
      const [mgt, fc, ds, pr, conv] = await Promise.all([
        axiosAuth().get("/analytics/management", { params: { period: mgtPeriod, center: centerParam || undefined, year: selectedYear || undefined } }),
        axiosAuth().get("/analytics/forecast", { params: { center: centerParam || undefined } }),
        axiosAuth().get("/analytics/dead-slots", { params: { center: centerParam || undefined } }),
        axiosAuth().get("/analytics/pressure", { params: { center: centerParam || undefined } }),
        axiosAuth().get("/analytics/conversion", { params: { center: centerParam || undefined } }),
      ]);
      setMgtData(mgt.data);
      setForecast(fc.data);
      setDeadSlots(ds.data);
      setPressure(pr.data);
      setConversion(conv.data);
      setTabStatus(s => ({ ...s, gerencia: "ok" }));
    } catch (e) {
      setTabStatus(s => ({ ...s, gerencia: _classifyError(e) }));
    }
  }, [axiosAuth, centerParam, mgtPeriod, selectedYear]);

  const loadAdvisors = useCallback(async () => {
    try {
      const [res, sumRes] = await Promise.all([
        axiosAuth().get("/analytics/advisors", { params: { center: centerParam || undefined } }),
        // Iter 216 — Carga también el resumen extendido por asesor.
        axiosAuth().get("/analytics/advisors-summary", {
          params: { period: mgtPeriod, year: selectedYear || undefined, center: centerParam || undefined },
        }).catch(() => ({ data: null })),
      ]);
      setAdvData(res.data);
      setAdvisorsSummary(sumRes.data);
      setTabStatus(s => ({ ...s, asesores: "ok" }));
    } catch (e) {
      setAdvData(null);
      setAdvisorsSummary(null);
      setTabStatus(s => ({ ...s, asesores: _classifyError(e) }));
    }
  }, [axiosAuth, centerParam, mgtPeriod, selectedYear]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadCenters();
      // Iter 162 — Cargar TODOS los endpoints en paralelo para determinar el
      // tabStatus completo desde el inicio (evita flicker y "stuck loading"
      // si el tab activo es uno permitido).
      await Promise.all([loadAlerts(), loadAppointments(), loadManagement(), loadAdvisors()]);
      setLoading(false);
    };
    init();
  }, [loadCenters, loadAlerts, loadAppointments, loadManagement, loadAdvisors]);

  // Load tab-specific data when tab changes.
  // Iter 216: centerParam NO se incluye en deps porque ya está dentro de cada
  // loader (a través de useCallback). Tenerlo aquí causaba double-fetch al
  // cambiar de centro (1 vez por centerParam, 1 vez por loader recreado).
  useEffect(() => {
    if (tab === "citas") { loadAppointments(); loadAlerts(); }
    if (tab === "gerencia") loadManagement();
    if (tab === "asesores") loadAdvisors();
  }, [tab, loadAppointments, loadAlerts, loadManagement, loadAdvisors]);

  // Iter 215/216 — Resumen del periodo (independiente). Se dispara en mount cuando
  // tab='citas' y en cualquier cambio de aptPeriod/selectedYear/centerParam.
  // En el tab 'gerencia' usa mgtPeriod en lugar de aptPeriod.
  useEffect(() => {
    if (tab === "citas") loadPeriodSummary(aptPeriod);
    else if (tab === "gerencia") loadPeriodSummary(mgtPeriod);
  }, [aptPeriod, mgtPeriod, selectedYear, tab, loadPeriodSummary]);

  // Iter 162 — Si el tab activo queda denegado, saltar al primer permitido
  useEffect(() => {
    if (tabStatus[tab] === "forbidden") {
      const fallback = ["citas", "gerencia", "asesores"].find(t => tabStatus[t] !== "forbidden");
      if (fallback) setTab(fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabStatus, tab]);

  const exportPdf = async () => {
    const typeMap = { citas: "citas", gerencia: "rendimiento", asesores: "asesores" };
    const reportType = typeMap[tab] || "citas";
    setExporting(true);
    try {
      const res = await axiosAuth().get("/analytics/report/pdf", {
        params: { report_type: reportType, center: centerParam || undefined },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `informe_${reportType}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Informe PDF descargado");
    } catch {
      toast.error("Error al generar el informe PDF");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="h-8 w-48 bg-slate-200 animate-pulse rounded" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-slate-100 animate-pulse rounded-sm" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="h-56 bg-slate-100 animate-pulse rounded-sm" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      {/* Header + Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase" data-testid="dashboard-title">
            Inteligencia de Negocio
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Control operativo y analiticas avanzadas</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedYear.toString()} onValueChange={v => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[80px] h-8 text-xs rounded-sm font-mono font-semibold" data-testid="year-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {buildYearOptions().map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={center} onValueChange={setCenter}>
            <SelectTrigger className="w-40 h-8 text-xs rounded-sm" data-testid="center-filter">
              <SelectValue placeholder="Centro" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los centros</SelectItem>
              {centers.map(c => (
                <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline" size="sm" className="h-8 text-xs rounded-sm"
            data-testid="refresh-btn"
            onClick={() => {
              if (tab === "citas") { loadAppointments(); loadAlerts(); }
              if (tab === "gerencia") loadManagement();
              if (tab === "asesores") loadAdvisors();
            }}
          >
            <RefreshCw className="w-3 h-3 mr-1" /> Actualizar
          </Button>
          {/* Iter 162 — Exportar PDF solo si hay al menos un tab accesible */}
          {(tabStatus.citas === "ok" || tabStatus.gerencia === "ok" || tabStatus.asesores === "ok") && (
          <Button
            size="sm" className="h-8 text-xs rounded-sm bg-red-600 hover:bg-red-700 text-white"
            data-testid="export-pdf-btn"
            disabled={exporting}
            onClick={exportPdf}
          >
            <FileDown className="w-3 h-3 mr-1" /> {exporting ? "Generando..." : "Exportar PDF"}
          </Button>
          )}
        </div>
      </div>

      {/* Iter 162 — Si ningún tab tiene permiso, mostrar mensaje único */}
      {tabStatus.citas === "forbidden" && tabStatus.gerencia === "forbidden" && tabStatus.asesores === "forbidden" && (
        <div className="rounded-sm border border-amber-200 bg-amber-50 p-6 text-center" data-testid="dashboard-no-access">
          <p className="text-sm text-amber-800 font-semibold">Sin permisos para ver Inteligencia de Negocio</p>
          <p className="text-xs text-amber-700 mt-1">Si necesitas acceso, contacta con un administrador.</p>
        </div>
      )}

      {/* Tabs */}
      {!(tabStatus.citas === "forbidden" && tabStatus.gerencia === "forbidden" && tabStatus.asesores === "forbidden") && (
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-slate-100 rounded-sm h-9" data-testid="dashboard-tabs">
          {tabStatus.citas !== "forbidden" && (
          <TabsTrigger value="citas" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-citas">
            <CalendarDays className="w-3.5 h-3.5 mr-1.5" /> Operación diaria
            {alerts.filter(a => a.level === "critical").length > 0 && (
              <span className="ml-1.5 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            )}
          </TabsTrigger>
          )}
          {tabStatus.gerencia !== "forbidden" && (
          <TabsTrigger value="gerencia" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-gerencia">
            <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Gerencia
          </TabsTrigger>
          )}
          {tabStatus.asesores !== "forbidden" && (
          <TabsTrigger value="asesores" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-asesores">
            <Users className="w-3.5 h-3.5 mr-1.5" /> Asesores
          </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="citas" className="mt-4">
          {tabStatus.citas === "forbidden" ? (
            <_TabFallback kind="forbidden" testId="citas-forbidden" />
          ) : tabStatus.citas === "error" ? (
            <_TabFallback kind="error" testId="citas-error" />
          ) : (
            <AppointmentsTab
              data={aptData}
              alerts={alerts}
              alertsLoading={alertsLoading}
              periodSummary={periodSummary}
              periodLoading={periodLoading}
              periodValue={aptPeriod}
              setPeriodValue={setAptPeriod}
              selectedCenter={centerParam}
              navigate={navigate}
            />
          )}
        </TabsContent>
        <TabsContent value="gerencia" className="mt-4">
          {tabStatus.gerencia === "forbidden" ? (
            <_TabFallback kind="forbidden" testId="gerencia-forbidden" />
          ) : tabStatus.gerencia === "error" ? (
            <_TabFallback kind="error" testId="gerencia-error" />
          ) : (
            <ManagementTab data={mgtData} forecast={forecast} deadSlots={deadSlots} pressure={pressure} conversion={conversion} period={mgtPeriod} onPeriodChange={setMgtPeriod} periodSummary={periodSummary} selectedCenter={centerParam} navigate={navigate} />
          )}
        </TabsContent>
        <TabsContent value="asesores" className="mt-4">
          {tabStatus.asesores === "forbidden" ? (
            <_TabFallback kind="forbidden" testId="asesores-forbidden" />
          ) : tabStatus.asesores === "error" ? (
            <_TabFallback kind="error" testId="asesores-error" />
          ) : (
            <AdvisorsTab data={advData} advisorsSummary={advisorsSummary} selectedCenter={centerParam} navigate={navigate} />
          )}
        </TabsContent>
      </Tabs>
      )}
    </div>
  );
}


// Iter 162 — Fallback inline para tabs sin permiso o con error
function _TabFallback({ kind, testId }) {
  const messages = {
    forbidden: {
      title: "Sin permisos para ver esta sección",
      sub: "Si necesitas acceso, contacta con un administrador.",
      color: "amber",
    },
    error: {
      title: "Error al cargar esta sección",
      sub: "Inténtalo de nuevo o contacta con soporte si persiste.",
      color: "red",
    },
  };
  const m = messages[kind] || messages.error;
  const cls = m.color === "amber"
    ? "border-amber-200 bg-amber-50 text-amber-800"
    : "border-red-200 bg-red-50 text-red-800";
  return (
    <div className={`rounded-sm border p-8 text-center ${cls}`} data-testid={testId}>
      <p className="text-sm font-semibold">{m.title}</p>
      <p className="text-xs mt-1 opacity-80">{m.sub}</p>
    </div>
  );
}
